import { Mail, Phone, MapPin, Quote } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect } from "react";

export default function BoTMessage({
  pageTitle = "Honorable Chairman, BoT | Bandarban University",
}) {

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  return (
    <div className="bg-slate-50 overflow-hidden">

      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-center justify-center">

        <div className="absolute inset-0 bg-gradient-to-br from-green-900 via-emerald-700 to-teal-600" />

        <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-300/10 rounded-full blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: .8 }}
          className="relative z-10 text-center text-white px-6 max-w-5xl"
        >
          <span className="bg-white/20 px-4 py-2 rounded-full backdrop-blur-md">
            Board of Trustees
          </span>

          <h1 className="text-5xl md:text-7xl font-bold mt-6 leading-tight">
            Message from the
            <span className="block text-yellow-300">
              Honorable Chairman, BoT
            </span>
          </h1>

          <p className="mt-6 text-xl text-gray-100 max-w-3xl mx-auto">
            Guiding Bandarban University towards excellence,
            innovation, and sustainable development.
          </p>
        </motion.div>
      </section>

      {/* PROFILE SECTION */}
      <section className="max-w-7xl mx-auto px-6 py-24">

        <div className="grid lg:grid-cols-5 gap-12 items-center">

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -80 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <div className="relative">

              <div className="absolute -inset-4 bg-gradient-to-r from-green-500 to-teal-500 rounded-3xl blur-xl opacity-30" />

              <img
                src="/assets/img/bot-chairman.jpeg"
                alt="Chairman"
                className="relative rounded-3xl shadow-2xl w-full object-cover"
              />

              <div className="bg-white/90 backdrop-blur-md p-4 rounded-xl shadow-lg">
                <h4 className="font-bold text-green-700">
                  Brig. Gen. (Retd.) Shah Md. Sultan Uddin Iqbal, Bir Protik, ndu, psc, MBA, PhD
                </h4>
                <p className="text-sm text-gray-600">
                  Chairman, Board of Trustees
                </p>
              </div>

            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 80 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-3"
          >
			<h4>بِسْمِ ٱللهِ ٱلرَّحْمَٰنِ ٱلرَّحِيْمِ</h4>
            <Quote className="text-green-600 mb-6" size={50} />
			
            <h2 className="text-4xl font-bold mb-6 text-slate-800">
              A Vision for Transformative Education
            </h2>

            <div className="space-y-6 text-gray-700 leading-8 text-justify">

              <p>
                It is our great honor & privilege to welcome you to our arena our much esteemed Bandarban University. It is an institution dedicated to academic excellence, innovation, and service specially for Chottogram hill tract region. As Chair of the Board of Trustees, I am honored to reflect on our achievements and share our aspirations for the future. Our university stands today on the strong foundation established by our visionary founders. Their foresight, commitment, and leadership transformed an ambitious vision into a respected center of learning. Through their dedication to quality education and institutional development, they created a legacy that continues to guide and inspire us.
              </p>

              <p>
                In a rapidly changing world, higher education plays a vital role in developing knowledge, skills, values, and leadership for sustainable progress. Our university remains committed to providing a transformative educational experience that empowers students (of the hill tract & surrounding area) to become responsible citizens, innovative thinkers, and future leaders. We continue to strengthen our academic programs, research activities, technological capabilities, and partnerships to ensure that our graduates are prepared to meet the challenges and opportunities of the twenty-first century.
              </p>

              <p>
                The Board of Trustees works closely with the university leadership, faculty, staff, students, alumni, and stakeholders to uphold the highest standards of academic excellence, integrity, accountability, and inclusiveness. Together, we all strive to foster a learning environment that encourages creativity, critical thinking, and lifelong learning. As we move forward, we remain committed to continuous improvement, innovation, and sustainable development. By building on our proud heritage and embracing new opportunities, we are confident that our university will continue to make meaningful contributions to all people of this area to compete at national & international level.
              </p>

              <p>
                On behalf of the Trust, I extend my sincere appreciation to all members of our university community for their dedication, trust, and support. Together, we will continue to shape a brighter future through education, research, and service.
              </p>

            </div>

          </motion.div>

        </div>
      </section>

      {/* QUOTE BLOCK */}
      <section className="py-20 bg-gradient-to-r from-green-700 to-teal-600 text-white">

        <motion.div
          initial={{ opacity: 0, scale: .95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-5xl mx-auto px-6 text-center"
        >
          <Quote className="mx-auto mb-6" size={60} />

          <h3 className="text-3xl md:text-4xl font-light italic leading-relaxed">
            “Together, we will continue to shape a brighter future
            through education, research, and service.”
          </h3>

          <p className="mt-6 text-xl">
            — Brig. Gen. (Retd.) Shah Md. Sultan Uddin Iqbal, Bir Protik, ndu, psc, MBA, PhD
          </p>
        </motion.div>

      </section>

      {/* VISION MISSION */}
      <section className="py-24 px-6">

        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8">

          {[
            {
              title: "Vision",
              text: "To become a leading institution of higher learning in Asia."
            },
            {
              title: "Mission",
              text: "To nurture creativity, innovation, research, and leadership."
            },
            {
              title: "Goals",
              text: "Expand research opportunities and strengthen global partnerships."
            }
          ].map((item, index) => (

            <motion.div
              key={index}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * .2 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl border border-gray-100"
            >
              <h3 className="text-2xl font-bold text-green-700 mb-4">
                {item.title}
              </h3>

              <p className="text-gray-600 leading-7">
                {item.text}
              </p>
            </motion.div>

          ))}

        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="relative py-24 overflow-hidden">

        <div className="absolute inset-0 bg-gradient-to-r from-green-700 to-teal-600" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center text-white">

          <h2 className="text-4xl font-bold mb-8">
            Office of the Chairman
          </h2>

          <div className="grid md:grid-cols-3 gap-8 mb-12">

            <div className="flex flex-col items-center">
              <MapPin size={30} />
              <p className="mt-3">
                K.B Road, Shualok, Bandarban
              </p>
            </div>

            <div className="flex flex-col items-center">
              <Phone size={30} />
              <p className="mt-3">
                +880 1318-305900
              </p>
            </div>

            <div className="flex flex-col items-center">
              <Mail size={30} />
              <p className="mt-3">
                chairman@bubban.ac.bd
              </p>
            </div>

          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: .95 }}
            className="bg-white text-green-700 px-8 py-4 rounded-full font-semibold shadow-xl"
          >
            Request Appointment
          </motion.button>

        </div>

      </section>

    </div>
  );
}